/**
 * The open project for this browser tab, shared by every tool on the page.
 *
 * It lives in sessionStorage, so it survives a refresh and is erased when the tab closes; nothing goes to a
 * server. Tools read it, change it, and subscribe to changes, so a guest list typed into one tool is there in
 * the next. `createProjectStore` takes the storage so it can be tested without a browser.
 */
import { emptyProject, parseProjectFile, serializeProject, type Project } from "./projectFile";
import type { ProjectData, RecordKind, RecordMap } from "./types";

export const PROJECT_STORAGE_KEY = "fdp-project-v1";

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface ProjectStore {
  get(): Project;
  set(next: Project | ((prev: Project) => Project)): void;
  /** Replace one list of records. */
  setRecords<K extends RecordKind>(kind: K, records: RecordMap[K][]): void;
  /** Add or replace records by id, keeping the rest. */
  upsertRecords<K extends RecordKind>(kind: K, records: RecordMap[K][]): void;
  reset(project?: Project): void;
  subscribe(listener: () => void): () => void;
}

export function createProjectStore(storage: StorageLike | null): ProjectStore {
  let current: Project | null = null;
  const listeners = new Set<() => void>();

  const load = (): Project => {
    if (storage) {
      try {
        const raw = storage.getItem(PROJECT_STORAGE_KEY);
        if (raw) { const r = parseProjectFile(raw); if (r.ok) return r.project; }
      } catch { /* private mode etc. */ }
    }
    return emptyProject();
  };
  const persist = (p: Project) => {
    if (!storage) return;
    try { storage.setItem(PROJECT_STORAGE_KEY, serializeProject(p)); } catch { /* storage full or blocked: the project still works this visit */ }
  };
  const commit = (p: Project) => { current = p; persist(p); Array.from(listeners).forEach((l) => l()); };

  const store: ProjectStore = {
    get: () => (current ??= load()),
    set: (next) => commit(typeof next === "function" ? next(store.get()) : next),
    setRecords: (kind, records) => store.set((p) => ({ ...p, data: { ...p.data, [kind]: records } as ProjectData })),
    upsertRecords: (kind, records) => store.set((p) => {
      const list = [...(p.data[kind] as { id: string }[])];
      for (const r of records as { id: string }[]) {
        const at = list.findIndex((x) => x.id === r.id);
        if (at >= 0) list[at] = r; else list.push(r);
      }
      return { ...p, data: { ...p.data, [kind]: list } as ProjectData };
    }),
    reset: (project) => { if (!project && storage) { try { storage.removeItem(PROJECT_STORAGE_KEY); } catch { /* ignore */ } } commit(project ?? emptyProject()); },
    subscribe: (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
  };
  return store;
}
